import { create } from 'zustand';

export interface Session {
  userId: string;
  accessToken: string;
  accessTokenExpiresAt: Date;
  /** A mandatory second factor has not been set up yet. */
  mustEnrollTotp: boolean;
}

interface SessionState {
  session: Session | null;
  /**
   * True until the silent refresh has answered. Rendering a login screen
   * during that window would throw out anyone who simply pressed reload.
   */
  isRestoring: boolean;
  signIn: (session: Session) => void;
  clear: () => void;
  finishRestoring: () => void;
  isAuthenticated: () => boolean;
  /** The enrolment flow confirmed a code; the warning banner can stand down. */
  markTotpEnrolled: () => void;
}

/**
 * The access token lives here, in memory, and nowhere else.
 *
 * Deliberately not persisted. Writing a bearer credential to localStorage puts
 * it within reach of every script on the page, which is exactly what the
 * httpOnly refresh cookie exists to prevent — keeping the refresh token safe
 * while leaving the access token lying about would be closing one door and
 * propping the other open.
 *
 * The cost is that a reload loses it. The cure is one silent refresh call
 * against the cookie, which the browser sends on its own.
 */
export const useSessionStore = create<SessionState>((set, get) => ({
  session: null,
  isRestoring: true,

  signIn: (session) => set({ session, isRestoring: false }),
  clear: () => set({ session: null, isRestoring: false }),
  finishRestoring: () => set({ isRestoring: false }),

  isAuthenticated: () => get().session !== null,

  markTotpEnrolled: () => {
    const { session } = get();

    if (session !== null) {
      set({ session: { ...session, mustEnrollTotp: false } });
    }
  },
}));
